import { fetchJSON } from 'omni-ui';

import { getEnvironment } from '../helpers/utils.js';

/**
 * @memberof ProfileAPI
 * @const {Object}
 * @readonly
 * @property {string} local - Local Environment URL
 * @property {string} dev - Dev Environment URL
 * @property {string} qa - QA Environment URL
 * @property {string} stg - Staging Environment URL
 * @property {string} prod - Production Environment URL
 */
const originsByEnv = {
  local: 'https://devomni.annalect.com/api/preferences',
  dev: 'https://devomni.annalect.com/api/preferences',
  qa: 'https://qaomni.annalect.com/api/preferences',
  stg: 'https://stgomni.annalect.com/api/preferences',
  prod: 'https://omni.annalect.com/api/preferences',
};

/**
 * Service for calls to the campaign
 * @property {string} env - Which environment to make calls to
 * @property {string} origin - Selected environment URL
 * @property {string} ansid - A property for keeping a default argument value for the methods
 * from the sso object on the window
 * @property {string} personId - A property for keeping a default argument value for the methods
 * from the sso object on the window
 */
export class PreferencesAPI {
  /**
   * @param {string} [env=getEnvironment()] - Key for {@link #.originsByEnv} to select
   * correct environment to make calls to
   */
  constructor(env = getEnvironment()) {
    this.env = env;
    this.origin = originsByEnv[env];

    this.ansid = window.AN?.sso?.session?.sid;
    this.personId = window.AN?.sso?.user?.personId;
  }

  /**
   * @param {Object} [personId=PreferencesAPI.personId] - id returned from valid ANsid call
   * @param {Object} [ansid=PreferencesAPI.ansid] - id returned from valid ANsid call
   * @returns {Promise}
   * @see SessionAPI.validateANsid
   */
  getClientPreferences(personId = this.personId, ANsid = this.ansid) {
    return fetchJSON(`${this.origin}/clients/${personId}?ANsid=${ANsid}`);
  }

  /**
   * @param {Object} [personId=PreferencesAPI.personId] - id returned from valid ANsid call
   * @param {Object} [ansid=PreferencesAPI.ansid] - id returned from valid ANsid call
   * @returns {Promise}
   * @see SessionAPI.validateANsid
   */
  getTeamsPreferences(personId = this.personId, ANsid = this.ansid) {
    return fetchJSON(`${this.origin}/teams/${personId}?ANsid=${ANsid}`);
  }

  /**
   * @param {Object} [personId=PreferencesAPI.personId] - id returned from valid ANsid call
   * @param {Object} [ansid=PreferencesAPI.ansid] - id returned from valid ANsid call
   * @returns {Promise}
   * @see SessionAPI.validateANsid
   */
  async getClientAndTeamsPreferences(
    personId = this.personId,
    ANsid = this.ansid
  ) {
    return Promise.allSettled([
      this.getClientPreferences(personId, ANsid),
      this.getTeamsPreferences(personId, ANsid),
    ]);
  }
}

// jsdoc requires classes not be exported via "export default class X"
export default PreferencesAPI;
